import { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Typography } from 'antd';
import { useNocLive } from '@/features/noc/hooks/useNocLive';
import type { NocStreamMode } from '@/features/noc/hooks/useNocStream';
import { CommandPanel } from '../components/CommandPanel';
import { ConnectPanel } from '../components/ConnectPanel';
import { InjectPanel } from '../components/InjectPanel';
import { ScenarioPanel } from '../components/ScenarioPanel';
import { SimFeed } from '../components/SimFeed';
import { StationPanel, type LabStation } from '../components/StationPanel';
import { TelemetryPanel } from '../components/TelemetryPanel';
import { simApi } from '../api/simApi';
import { useStations } from '@/features/cargadores/hooks/useStations';
import { useSimulation } from '../hooks/useSimulation';
import { useStationLive } from '../hooks/useStationLive';

function toStreamMode(mode: ReturnType<typeof useNocLive>['mode']): NocStreamMode {
  if (mode === 'sse') {
    return 'open';
  }
  return mode === 'connecting' ? 'connecting' : 'unavailable';
}

/**
 * Laboratorio EVSE (US 2.6, TG-97, ADR 0029).
 *
 * Dos columnas: controles (estación, downlink, uplink y escenarios) y
 * feed/telemetría (SSE en vivo). Reutiliza el stream del NOC con `useNocLive`.
 */
export function LaboratorioPage() {
  const live = useNocLive();
  const [selectedStationId, setSelectedStationId] = useState<string | null>(null);
  const [selectedConnectorId, setSelectedConnectorId] = useState<number | null>(1);
  // El selector lista los cargadores del CRM (alta), no el inventario del core.
  const stations = useStations({ limit: 100 });
  const [injectionAllowlist, setInjectionAllowlist] = useState<string[]>([]);

  useEffect(() => {
    let active = true;
    void simApi
      .config()
      .then((config) => {
        if (active) setInjectionAllowlist(config.injectionAllowlist);
      })
      .catch(() => {
        if (active) setInjectionAllowlist([]);
      });
    return () => {
      active = false;
    };
  }, []);

  // Unión de fuentes: lo registrado en Cargadores + las estaciones que sólo
  // existen en el core (las de simulación por defecto). Estas últimas se
  // mantienen visibles para probar el rechazo del alta.
  const labStations = useMemo<LabStation[]>(() => {
    const registered: LabStation[] = stations.data.map((station) => ({
      id: station.id,
      name: station.location?.name ?? null,
      protocol: station.protocol ?? null,
      vendor: station.vendor ?? null,
      model: station.model ?? null,
      registered: true,
      registryStatus: station.registryStatus ?? null,
      operationStatus: station.operationStatus ?? null,
      operationReason: station.operationReason ?? null,
      isOnline: station.isOnline ?? false,
    }));
    const ids = new Set(registered.map((station) => station.id));
    const coreOnly: LabStation[] = live.stations
      .filter((station) => !ids.has(station.stationId))
      .map((station) => ({
        id: station.stationId,
        name: station.name ?? null,
        protocol: station.protocol ?? null,
        vendor: station.vendor ?? null,
        model: station.model ?? null,
        registered: false,
        registryStatus: null,
        operationStatus: null,
        operationReason: null,
        isOnline: station.liveness === 'ONLINE',
      }));
    return [...registered, ...coreOnly];
  }, [stations.data, live.stations]);

  // Auto-selecciona la primera estación cuando llega el listado.
  useEffect(() => {
    if (!selectedStationId && labStations.length > 0) {
      const first = labStations[0];
      if (first) {
        setSelectedStationId(first.id);
      }
    }
  }, [labStations, selectedStationId]);

  const { detail } = useStationLive(selectedStationId);
  const simulation = useSimulation(live.streamEvents);

  const connectors = detail?.connectorDetails ?? [];
  const activeConnector =
    connectors.find((connector) => connector.connectorId === selectedConnectorId) ?? connectors[0] ?? null;
  const station = labStations.find((item) => item.id === selectedStationId) ?? null;
  const injectionAllowed =
    selectedStationId !== null && injectionAllowlist.includes(selectedStationId);

  return (
    <div className="lab-page">
      <div>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Laboratorio EVSE
        </Typography.Title>
        <Typography.Text type="secondary">
          Emite comandos OCPP al core, inyecta mensajes desde el VCP y observa el feed en vivo
          (ADR 0029). Solo entorno de simulación.
        </Typography.Text>
      </div>

      {live.error && <Alert type="error" showIcon message={live.error} />}

      <div className="lab-grid">
        <div className="lab-col">
          <Card title="Estación y conector" size="small">
            <StationPanel
              stations={labStations}
              selectedStationId={selectedStationId}
              onSelectStation={(stationId) => {
                setSelectedStationId(stationId);
                setSelectedConnectorId(1);
              }}
              selectedConnectorId={selectedConnectorId}
              onSelectConnector={setSelectedConnectorId}
              detail={detail}
              loading={stations.loading || live.loading}
            />
          </Card>

          <Card title="Conectar cargador (simulación de alta)" size="small">
            <ConnectPanel defaultStationId={selectedStationId} onConnected={stations.refresh} />
          </Card>

          <Card title="Emitir al EVSE (downlink)" size="small">
            <CommandPanel
              stationId={selectedStationId}
              connectorId={activeConnector?.connectorId ?? 1}
              protocol={station?.protocol ?? null}
            />
          </Card>

          <Card title="Inyectar desde el EVSE (uplink)" size="small">
            <InjectPanel
              stationId={selectedStationId}
              connectorId={activeConnector?.connectorId ?? 1}
              allowed={injectionAllowed}
              allowlist={injectionAllowlist}
            />
          </Card>

          <Card title="Escenarios de un clic" size="small">
            <ScenarioPanel
              catalog={simulation.catalog}
              loading={simulation.catalogLoading}
              error={simulation.catalogError}
              runs={simulation.runs}
              startingId={simulation.startingId}
              onStart={simulation.startScenario}
              onReload={simulation.reloadCatalog}
            />
          </Card>
        </div>

        <div className="lab-col">
          <Card title="Telemetría en vivo" size="small">
            <TelemetryPanel
              station={detail}
              connector={activeConnector}
              sessions={detail?.sessions ?? []}
            />
          </Card>

          <Card title="Feed de eventos (SSE)" size="small">
            <SimFeed
              events={live.streamEvents}
              mode={toStreamMode(live.mode)}
              onClear={live.clearStream}
            />
          </Card>
        </div>
      </div>
    </div>
  );
}
